// Section 9 — Appointment classification helpers.
//
// Extracted into a plain-JS module so:
//   1) The rules are testable without React / DOM,
//   2) Other admin views (e.g. dashboards, Customer Detail) can reuse them.
//
// Melbourne "today" in YYYY-MM-DD form. Date-only comparison keeps the split
// deterministic regardless of the appointment `time`.
export function melbourneTodayISO(now = new Date()) {
  try {
    return now.toLocaleString("en-CA", {
      timeZone: "Australia/Melbourne",
      year: "numeric", month: "2-digit", day: "2-digit",
    });
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

// Statuses that unambiguously belong in History.
export const HISTORY_STATUSES = new Set([
  "completed", "converted", "closed", "no_show",
  "cancelled", "customer_cancelled", "admin_cancelled",
]);

// Statuses that stay in Upcoming/Active regardless of date (require action).
export const ALWAYS_ACTIVE_STATUSES = new Set(["follow_up"]);

// Statuses that count as "an active replacement is still pending".
export const REPLACEMENT_ACTIVE_STATUSES = new Set(["confirmed", "pending", "follow_up"]);

/**
 * Returns "upcoming" | "history" | "hidden" for each appointment.
 *
 *  - HISTORY_STATUSES → history.
 *  - follow_up → upcoming (requires action).
 *  - rescheduled → history unless another active appt exists for the same
 *    trial_lead_id (or same customer_email as a fallback) — in which case
 *    the original is hidden because the replacement is already in Upcoming.
 *  - Anything else → upcoming while today or later, history once the date
 *    has passed (Australia/Melbourne calendar day).
 */
export function classifyAppointment(appt, allAppts, todayISO) {
  const status = (appt?.status || "confirmed").toLowerCase();
  if (ALWAYS_ACTIVE_STATUSES.has(status)) return "upcoming";
  if (HISTORY_STATUSES.has(status)) return "history";
  if (status === "rescheduled") {
    const hasActiveReplacement = (allAppts || []).some(a => {
      if (a.id === appt.id) return false;
      const s = (a.status || "").toLowerCase();
      if (!REPLACEMENT_ACTIVE_STATUSES.has(s)) return false;
      if (appt.trial_lead_id && a.trial_lead_id) return a.trial_lead_id === appt.trial_lead_id;
      if (appt.customer_email && a.customer_email) {
        return a.customer_email === appt.customer_email
          && a.appointment_type === appt.appointment_type;
      }
      return false;
    });
    return hasActiveReplacement ? "hidden" : "history";
  }
  const dateStr = appt?.date || "";
  if (dateStr && dateStr < todayISO) return "history";
  return "upcoming";
}

/** Split a full list into buckets in one pass. */
export function bucketAppointments(list, todayISO = melbourneTodayISO()) {
  const buckets = { upcoming: [], history: [], hidden: [] };
  for (const a of list || []) {
    buckets[classifyAppointment(a, list, todayISO)].push(a);
  }
  return buckets;
}
